import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import api from '../services/api';

const AnalyticsContext = createContext(null);

// Our own hosts (any subdomain). document.referrer from these is internal navigation.
const OWN_DOMAINS = ['medigomap.com', 'mymedicalmap.com', 'medigomap.onrender.com'];
// A session ends after this much inactivity (checked on full page load)
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

const SESSION_ID_KEY = 'analyticsSessionId';
const SESSION_ATTRIBUTION_KEY = 'analyticsAttribution';
const SESSION_LAST_ACTIVITY_KEY = 'analyticsLastActivity';

// Storage can throw (private mode, blocked site data) — never let that break the app
const storageGet = (storage, key) => {
  try {
    return window[storage].getItem(key);
  } catch {
    return null;
  }
};

const storageSet = (storage, key, value) => {
  try {
    window[storage].setItem(key, value);
  } catch {
    // ignore
  }
};

const isOwnDomain = (url) => {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
    if (hostname === window.location.hostname.toLowerCase()) return true;
    return OWN_DOMAINS.some(d => hostname === d || hostname.endsWith('.' + d));
  } catch {
    return false;
  }
};

// Capture UTM params, click ids and external referrer from the landing URL
const captureAttribution = () => {
  const params = new URLSearchParams(window.location.search);
  const utmData = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(key => {
    const val = params.get(key);
    if (val) utmData[key] = val;
  });
  // Google Ads / Meta click IDs
  if (params.get('gclid')) utmData.gclid = params.get('gclid');
  if (params.get('fbclid')) utmData.fbclid = params.get('fbclid');
  // Capture referrer only when it is an external site
  if (document.referrer && !isOwnDomain(document.referrer)) utmData.referrer = document.referrer;
  return utmData;
};

// Resolve visitor/session ids and first-touch attribution synchronously, so the
// first tracking calls (which come from child page effects) already have them.
const initTrackingState = () => {
  let visitorId = storageGet('localStorage', 'visitorId');
  if (!visitorId) {
    visitorId = uuidv4();
    storageSet('localStorage', 'visitorId', visitorId);
  }

  let sessionId = storageGet('sessionStorage', SESSION_ID_KEY);
  const lastActivity = parseInt(storageGet('sessionStorage', SESSION_LAST_ACTIVITY_KEY), 10);
  const expired = !lastActivity || Date.now() - lastActivity > SESSION_TIMEOUT_MS;

  let attribution = null;
  if (sessionId && !expired) {
    try {
      attribution = JSON.parse(storageGet('sessionStorage', SESSION_ATTRIBUTION_KEY) || 'null');
    } catch {
      attribution = null;
    }
  }

  if (!sessionId || expired || !attribution) {
    if (!sessionId || expired) sessionId = uuidv4();
    attribution = captureAttribution();
    storageSet('sessionStorage', SESSION_ID_KEY, sessionId);
    storageSet('sessionStorage', SESSION_ATTRIBUTION_KEY, JSON.stringify(attribution));
  }
  storageSet('sessionStorage', SESSION_LAST_ACTIVITY_KEY, Date.now().toString());

  return { visitorId, sessionId, attribution };
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

export const AnalyticsProvider = ({ children }) => {
  const visitorIdRef = useRef(null);
  const sessionIdRef = useRef(null);
  const searchCountRef = useRef(null);
  // UTM params / click ids / referrer captured on landing (first touch for the session)
  const utmDataRef = useRef(null);
  // Serializes tracking requests so the first call creates the Visitor doc
  // before later calls look it up (parallel first calls would create duplicates)
  const queueRef = useRef(Promise.resolve());
  // Set when a page tracks its own pageview, so the provider doesn't add a second one
  const pageViewTrackedRef = useRef(false);

  // Initialize before children render — child effects run before this provider's effects
  if (visitorIdRef.current === null) {
    const { visitorId, sessionId, attribution } = initTrackingState();
    visitorIdRef.current = visitorId;
    sessionIdRef.current = sessionId;
    utmDataRef.current = attribution;
  }
  if (searchCountRef.current === null) {
    const storedCount = storageGet('sessionStorage', 'searchCount');
    searchCountRef.current = storedCount ? parseInt(storedCount, 10) || 0 : 0;
  }

  const track = useCallback((event, data = {}) => {
    const payload = {
      visitorId: visitorIdRef.current,
      sessionId: sessionIdRef.current,
      event,
      data,
      // Include UTM/source data with every tracking call
      ...(utmDataRef.current && Object.keys(utmDataRef.current).length > 0
        ? { attribution: utmDataRef.current }
        : {})
    };
    storageSet('sessionStorage', SESSION_LAST_ACTIVITY_KEY, Date.now().toString());
    queueRef.current = queueRef.current
      .then(() => api.post('/api/analytics/track', payload))
      .catch((err) => {
        // Silent fail for analytics
        console.debug('Analytics tracking failed:', err);
      });
    return queueRef.current;
  }, []);

  const trackPageView = useCallback((path, title) => {
    pageViewTrackedRef.current = true;
    track('pageview', { path, title });
  }, [track]);

  useEffect(() => {
    // Track initial pageview only if the landing page didn't track its own
    // (page effects run before this one)
    if (!pageViewTrackedRef.current) {
      trackPageView(window.location.pathname, document.title);
    }
  }, [trackPageView]);

  const trackSearch = useCallback((query, filters, resultsCount) => {
    searchCountRef.current += 1;
    storageSet('sessionStorage', 'searchCount', searchCountRef.current.toString());
    track('search', { query, filters, resultsCount });
  }, [track]);

  const trackFacilityView = useCallback((facilityId) => {
    track('facility_view', { facilityId });
  }, [track]);

  const trackSubscribe = useCallback((email) => {
    track('subscribe', { email });
  }, [track]);

  const trackClick = useCallback((actionType, details = {}) => {
    track('click', { actionType, ...details });
  }, [track]);

  const getSearchCount = useCallback(() => {
    return searchCountRef.current;
  }, []);

  const shouldShowSubscribePopup = useCallback(() => {
    const hasSubscribed = storageGet('localStorage', 'hasSubscribed');
    const hasDismissed = storageGet('sessionStorage', 'popupDismissed');
    return searchCountRef.current >= 1 && !hasSubscribed && !hasDismissed;
  }, []);

  const markAsSubscribed = useCallback(() => {
    storageSet('localStorage', 'hasSubscribed', 'true');
  }, []);

  const dismissPopup = useCallback(() => {
    storageSet('sessionStorage', 'popupDismissed', 'true');
  }, []);

  const value = useMemo(() => ({
    trackPageView,
    trackSearch,
    trackFacilityView,
    trackSubscribe,
    trackClick,
    getSearchCount,
    shouldShowSubscribePopup,
    markAsSubscribed,
    dismissPopup
  }), [trackPageView, trackSearch, trackFacilityView, trackSubscribe, trackClick,
    getSearchCount, shouldShowSubscribePopup, markAsSubscribed, dismissPopup]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
